import React, { useCallback, useEffect, useMemo, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";

import Map, {
  AttributionControl,
  Layer,
  MapEvent,
  MapLayerMouseEvent,
  MapStyleDataEvent,
  MapboxGeoJSONFeature,
  Popup,
  Source,
  ViewStateChangeEvent,
} from "react-map-gl";
import { FeatureCollection, Point } from 'geojson';
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  getAreaLayers,
  getDistrictLayers,
  getSectorLayers,
  pointStyle,
} from "./layers";
import { useMapStyle } from "./config";
import { Overlay } from "./InsightSiteMapOverlay";
import { useSites } from "@/hooks";
import { LocationResult, PenPortraitInsightVariable, SiteInfo } from "@/types";
import { extractBreaks, getFillColor } from "@/utils";

type InsightMapProps = {
  locationScores: LocationResult;
  siteScores?: PenPortraitInsightVariable[];
};

const InsightSiteMap: React.FC<InsightMapProps> = ({ locationScores, siteScores }) => {
  const theme = useTheme();
  const mapStyle = useMapStyle(theme.palette.mode);

  // Prepare Site Data
  const {
    sites, loading: sitesLoading, error: sitesError
  } = useSites();

  const siteData  = useMemo<FeatureCollection>(() => {
    if (!sites || sites.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }

    return {
      type: 'FeatureCollection',
      features: sites.map((site) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [site.longitude, site.latitude],
        },
        properties: {
          site_id: site.site_id,
          site_name: site.site_name
        },
      })),
    };
  }, [sites]);

  // Extract geo layers
  const areaLayers = getAreaLayers(theme);
  const districtLayers = getDistrictLayers(theme);
  const sectorLayers = getSectorLayers(theme);

  // Prepare selection & hover state
  const [selected, setSelected] = useState<MapboxGeoJSONFeature | undefined>(
    undefined,
  );
  const [selectedSites, setSelectedSites] = useState<number[]>([]);
  const [hovered, setHovered] = useState<number | string | undefined>(0);
  const [siteInfo, setSiteInfo] = useState<SiteInfo | null>(null);

  const [cursor, setCursor] = useState<string>("auto");
  const [breaks, setBreaks] = useState<number[]>([]);
  const [firstTime, setFirstTime] = useState<boolean>(true);

  const siteFilter = useMemo(() => {
    if (selectedSites.length === 0) return undefined;
    return ['in', ['get', 'site_id'], ['literal', selectedSites]];
  }, [selectedSites]);

  useEffect(() => {
    siteScores && setSelectedSites(siteScores.slice(0, 20).map((site) => site.tag_id));
  }, [siteScores]);

  const maxValue = Math.max.apply(
    Math,
    (siteScores || []).map((x) => Number(x.score_wt)),
  );
  const minValue = Math.min.apply(
    Math,
    (siteScores || []).map((x) => Number(x.score_wt)),
  );

  const onLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      map &&
        locationScores.area.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "area-base",
          "fill-color",
          getFillColor(extractBreaks(locationScores.area), theme.palette.gradient),
        );
      map &&
      locationScores.district.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "district-source",
              sourceLayer: "postal_district_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "district-base",
          "fill-color",
          getFillColor(extractBreaks(locationScores.district), theme.palette.gradient),
        );
      map &&
      locationScores.sector.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "sector-source",
              sourceLayer: "postal_sector_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "sector-base",
          "fill-color",
          getFillColor(extractBreaks(locationScores.sector), theme.palette.gradient),
        );
      setBreaks(extractBreaks(locationScores.area));
    },
    [locationScores, theme.palette.gradient],
  );

  useEffect(() => {
    setBreaks(extractBreaks(locationScores.area));
  }, [locationScores]);

  const onClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (feature && feature.state.name) {
      setSelected(feature);
    } else {
      clearSelected();
    }
  }, []);

  const onHover = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (feature) {
      if (feature.layer.id === 'point' && feature['properties']) {
        setHovered(0);
        const coordinates = (feature.geometry as Point).coordinates.slice();
        setSiteInfo({
          longitude: coordinates[0],
          latitude: coordinates[1],
          name: feature.properties?.site_name || ''
        })
      } else if (feature.state.name){
        setHovered(feature.id);
      } else {
        setHovered(0);
      }
    } else {
      setHovered(0);
    }
  }, []);

  const onZoom = useCallback(
    (event: ViewStateChangeEvent) => {
      const zoom = event.viewState.zoom;
      if (zoom > 9) {
        setBreaks(extractBreaks(locationScores.sector));
      } else if (zoom > 7) {
        setBreaks(extractBreaks(locationScores.district));
      } else {
        setBreaks(extractBreaks(locationScores.area));
      }
    },
    [locationScores],
  );

  const onStyleData = useCallback((event: MapStyleDataEvent) => {
    setFirstTime(false);
  }, []);

  const clearSelected = () => {
    setSelected(undefined);
  };

  const handleSiteSelect = (sites: number[]) => {
    setSelectedSites(sites);
  }

  const handleSiteHover = (site: number | null) => {
    if (site) {
      const siteMatch = sites.find(s => s.site_id === site)
      if (siteMatch) {
        setSiteInfo({name: siteMatch.site_name, latitude: siteMatch.latitude, longitude: siteMatch.longitude});
      }
    } else {
      setSiteInfo(null);
    }
  }

  const handleSiteDelete = (site: number) => {
    setSelectedSites(selectedSites.filter(s => s !== site));
  }

  const onMouseEnter = useCallback(() => setCursor("pointer"), []);
  const onMouseLeave = useCallback(() => setCursor("auto"), []);

  const hoverFilter = useMemo(() => ["==", ["id"], hovered], [hovered]);
  const selectFilter = useMemo(
    () => ["==", ["id"], selected ? selected.id : 0],
    [selected],
  );

  return (
    <Box
      height="100%"
      minHeight="750px"
      width="100%"
      position="relative"
      sx={{
        overflow: "hidden",
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        ".mapbox-improve-map": {
          display: "none",
        },
        ".mapboxgl-ctrl.mapboxgl-ctrl-attrib": {
          backgroundColor: "transparent !important",
        },
        ".mapboxgl-ctrl-attrib-inner": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-ctrl-attrib a": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-popup-content": {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
          padding: "4px",
          borderRadius: "6px",
          textAlign: "center"
        },
        ".mapboxgl-popup-anchor-top .mapboxgl-popup-tip": {
          borderBottomColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip": {
          borderTopColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-left .mapboxgl-popup-tip": {
          borderRightColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-right .mapboxgl-popup-tip": {
          borderLeftColor: `${theme.palette.info.main} !important`
        }
      }}
    >
      <Map
        initialViewState={{
          longitude: -0,
          latitude: 54.8,
          zoom: 5,
        }}
        projection={{ name: "mercator" }}
        minZoom={4}
        maxBounds={[
          [-30, 36],
          [30, 66],
        ]}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_MAP_TOKEN}
        cursor={cursor}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        onLoad={onLoad}
        onMouseMove={onHover}
        onZoomEnd={onZoom}
        onStyleData={onStyleData}
        interactiveLayerIds={["area-base", "district-base", "sector-base", "point"]}
        attributionControl={false}
        touchPitch={false}
        dragRotate={false}
      >
        <AttributionControl customAttribution=" " />
        <Source
          id="area-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_area_test"
        >
          <Layer beforeId="waterway-label" {...areaLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...areaLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...areaLayers.select}
            filter={selectFilter}
          />
        </Source>
        <Source
          id="district-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_district_test"
        >
          <Layer beforeId="waterway-label" {...districtLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...districtLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...districtLayers.select}
            filter={selectFilter}
          />
        </Source>
        <Source
          id="sector-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_sector_test"
        >
          <Layer beforeId="waterway-label" {...sectorLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...sectorLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...sectorLayers.select}
            filter={selectFilter}
          />
        </Source>
        <Source
          key={"clustered"}
          id="point-source"
          type="geojson"
          data={siteData}
          maxzoom={24}
          buffer={0}
          tolerance={0}
          cluster={false}
          clusterMaxZoom={14}
          clusterRadius={20}
          clusterMinPoints={5}
        >
          {/*<Layer {...clusterLayer}/>*/}
          {/*<Layer {...clusterCountLayer}/>*/}
          <Layer {...pointStyle} filter={siteFilter}/>
        </Source>
        {siteInfo && (
          <Popup
            latitude={siteInfo.latitude}
            longitude={siteInfo.longitude}
            closeButton={false}
            onClose={() => setSiteInfo(null)}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.palette.info.contrastText }}
            >
              {siteInfo.name}
            </Typography>
          </Popup>
        )}
        {sitesError && (
          <Alert
            variant="filled"
            severity="error"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
            }}>
            <AlertTitle>Unable to Load Sites</AlertTitle>
            It was not possible to load your sites at this time.
            Please try again and contact the support team if the problem persists.
          </Alert>
        )}
        <Overlay
          sites={sites}
          selectedSites={selectedSites}
          scores={locationScores}
          selected={selected}
          clearSelected={clearSelected}
          breaks={breaks}
          firstTime={firstTime}
          siteScores={siteScores?.filter(x => selectedSites.includes(x.tag_id)) || []}
          min={minValue}
          max={maxValue}
          setSites={handleSiteSelect}
          siteInfo={siteInfo}
          onSiteHover={handleSiteHover}
          onSiteDelete={handleSiteDelete}
        />
      </Map>
    </Box>
  );
};

export default InsightSiteMap;
